"use client";

import React, { useState, useEffect } from "react";
import { useReducedMotion } from "framer-motion";
import { MapPin, Phone, Navigation, Compass, Building2, CircleCheck as CheckCircle2, ChevronRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
interface CenterItem {
  id: string;
  name: string;
  badge: {
    vi: string;
    en: string;
  };
  address: string;
  phone: string;
  image: string;
  srcset: string;
  sizes: string;
  directionsUrl: string;
  specs: {
    vi: string;
    en: string;
  };
}
const centersData: CenterItem[] = [{
  id: "center-tan-mai",
  name: "ROBOTO Tân Mai",
  badge: {
    vi: "Cơ sở Flagship Hoàng Mai",
    en: "Flagship Campus"
  },
  address: "Số 9 Louis II, KĐT Louis City Hoàng Mai, Hoàng Mai, Hà Nội",
  phone: "0346 386 986",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_tech_city.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_tech_city.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_tech_city.png-wvc-sizes",
  directionsUrl: "https://maps.google.com/?q=So+9+Louis+II+KDT+Louis+City+Hoang+Mai+Ha+Noi",
  specs: {
    vi: "Phòng Lab VEX & LEGO Robotics • Đấu trường thi đấu chuẩn quốc tế",
    en: "VEX & LEGO Robotics Lab • International Arena"
  }
}, {
  id: "center-xa-dan",
  name: "ROBOTO Xã Đàn",
  badge: {
    vi: "Trung tâm Đào tạo Trọng điểm",
    en: "Central Training Hub"
  },
  address: "199 Xã Đàn, Đống Đa, Hà Nội",
  phone: "0346 698 986",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_silicon_grove.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_silicon_grove.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_silicon_grove.png-wvc-sizes",
  directionsUrl: "https://maps.google.com/?q=199+Xa+Dan+Dong+Da+Ha+Noi",
  specs: {
    vi: "Phòng chuyên đề Coding & AI • Xưởng chế tạo Maker Studio",
    en: "Coding & AI Suites • STEM Maker Studio"
  }
}, {
  id: "center-tam-trinh",
  name: "ROBOTO Tam Trinh",
  badge: {
    vi: "Trung tâm Sáng tạo Trẻ",
    en: "Junior Innovation Hub"
  },
  address: "1611 - 3-01 Khu Gelexia - Geleximco, Hoàng Mai, Hà Nội",
  phone: "0342 168 188",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_harbor_district.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_harbor_district.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_harbor_district.png-wvc-sizes",
  directionsUrl: "https://maps.google.com/?q=1611+3-01+Khu+Gelexia+Geleximco+Hoang+Mai+Ha+Noi",
  specs: {
    vi: "Không gian STEAM Khám phá • Xưởng lắp ráp LEGO Education",
    en: "STEAM Discovery Lab • LEGO Education Workshop"
  }
}, {
  id: "center-hoang-quoc-viet",
  name: "ROBOTO Hoàng Quốc Việt",
  badge: {
    vi: "Học viện Công nghệ Cầu Giấy",
    en: "Cau Giay Tech Academy"
  },
  address: "136 Hoàng Quốc Việt, phường Nghĩa Đô, Hà Nội",
  phone: "0342 968 986",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_tech_city.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_tech_city.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_tech_city.png-wvc-sizes",
  directionsUrl: "https://maps.google.com/?q=136+Hoang+Quoc+Viet+Nghia+Do+Ha+Noi",
  specs: {
    vi: "Phòng Thực hành Engineering • Lắp ráp mạch vi điều khiển & Robotics",
    en: "Engineering Fabrication Lab • Circuitry & Robotics"
  }
}];
const copyDict = {
  eyebrow: {
    vi: "HỆ THỐNG CƠ SỞ & PHÒNG LAB CÔNG NGHỆ",
    en: "REGIONAL CAMPUSES & LABS"
  },
  heading: {
    vi: "Tìm cơ sở học tập gần bạn nhất",
    en: "Find your nearest learning center"
  },
  description: {
    vi: "Kính mời phụ huynh đưa con đến trải nghiệm các phòng lab công nghệ chuyên sâu để thấy rõ môi trường học sinh trực tiếp sáng tạo, lập trình và chế tạo robot. Mỗi cơ sở đều quy tụ giảng viên chuẩn quốc tế, tỷ lệ kèm cặp tối ưu 1:6 cùng trang thiết bị chuẩn hãng LEGO Education và VEX Robotics.",
    en: "Visit our dedicated Robotics and Engineering labs to see where students build, test, and compete. Every location features certified mentors, low 1:6 student ratios, and industry-grade fabrication tools."
  },
  mapTitle: {
    vi: "Bản đồ vị trí cơ sở",
    en: "Campus Location Map"
  },
  activeBadge: {
    vi: "4 Cơ sở hoạt động",
    en: "4 Active Locations"
  },
  currentlyFocused: {
    vi: "Cơ sở đang xem",
    en: "Currently Focused"
  },
  selectedBadge: {
    vi: "Đang chọn",
    en: "Selected"
  },
  hotlineLabel: {
    vi: "Hotline:",
    en: "Phone:"
  },
  getDirections: {
    vi: "Chỉ đường",
    en: "Get directions"
  },
  viewDetails: {
    vi: "Xem chi tiết cơ sở",
    en: "View center details"
  },
  bookTrial: {
    vi: "Đăng ký học thử",
    en: "Book trial"
  },
  tourHeading: {
    vi: "Ba mẹ muốn đưa con tham quan trực tiếp cơ sở trước?",
    en: "Prefer an in-person facility tour first?"
  },
  tourDesc: {
    vi: "Trực quan không gian phòng lab Robotics, tham quan lớp học đang diễn ra và cùng giảng viên đánh giá năng lực, xây dựng lộ trình Coding và STEAM bài bản cho con.",
    en: "Walk through our Robotics labs, observe an active class, and discuss your child's learning pathway with an instructor."
  },
  tourCta: {
    vi: "Đặt lịch trải nghiệm lab",
    en: "Schedule lab visit"
  }
};
const defaultMascot = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/c2386fca-8209-4748-ae37-47d275ce8104.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/c2386fca-8209-4748-ae37-47d275ce8104.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/c2386fca-8209-4748-ae37-47d275ce8104.png-wvc-sizes"
};
interface MatchedProgramBadgesProps {
  programsOffered?: string;
}
function MatchedProgramBadges({
  programsOffered
}: MatchedProgramBadgesProps) {
  const {
    posts: programPosts
  } = usePosts();
  if (!programsOffered || typeof programsOffered !== "string" || !programPosts || programPosts.length === 0) {
    return null;
  }
  const normalizedOffered = programsOffered.toLowerCase();
  const matchedPrograms = programPosts.filter((prog: any) => {
    const progName = prog?.acf?.program_name || prog?.customFields?.program_name;
    if (typeof progName === "string" && progName.trim()) {
      return normalizedOffered.includes(progName.trim().toLowerCase());
    }
    return false;
  });
  if (matchedPrograms.length === 0) return null;
  return <div className="flex flex-wrap items-center gap-1.5 pt-1" onClick={e => e.stopPropagation()}>
      {matchedPrograms.map((prog: any, i: number) => {
      const displayName = prog?.acf?.program_name || prog?.customFields?.program_name || prog.title;
      return <Link key={prog.id} data-index={i} to={prog.link || "#"} className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-mono font-medium bg-secondary/10 text-secondary hover:bg-secondary/20 border border-secondary/25 transition-colors">
            {displayName}
          </Link>;
    })}
    </div>;
}
interface CenterCardsListProps {
  selectedCenterId: string;
  setSelectedCenterId: (id: string) => void;
  lang: 'vi' | 'en';
}
function CenterCardsList({
  selectedCenterId,
  setSelectedCenterId,
  lang
}: CenterCardsListProps) {
  const {
    posts
  } = usePosts();
  return <>
      {centersData.map((center, index) => {
      const isSelected = center.id === selectedCenterId;

      // Match center post by comparing post.acf.center_name (case-insensitive) to center.name
      const matchedPost = posts?.find((post: any) => {
        const acfCenterName = post?.acf?.center_name || post?.customFields?.center_name;
        if (typeof acfCenterName === "string" && acfCenterName.trim()) {
          return acfCenterName.trim().toLowerCase() === center.name.trim().toLowerCase();
        }
        return false;
      });
      const programsOffered = matchedPost?.acf?.programs_offered || matchedPost?.customFields?.programs_offered;
      return <div key={center.id} id={center.id} data-index={index} onClick={() => setSelectedCenterId(center.id)} className="cursor-pointer scroll-mt-28">
            <Card className={`transition-[border-color,box-shadow,background-color] duration-200 border-2 overflow-hidden py-0 gap-0 ${isSelected ? "border-secondary bg-card shadow-lg ring-1 ring-secondary/20" : "border-border bg-card/70 hover:border-secondary/50 hover:bg-card shadow-sm"}`}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
                {/* Facility Exterior Photo */}
                <div className="md:col-span-5 relative aspect-[16/10] md:aspect-auto overflow-hidden bg-muted">
                  <img src={center.image} data-wvc-srcset={center.srcset} data-wvc-sizes={center.sizes} alt={`${center.name} building exterior`} loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-[transform] duration-300 hover:scale-105" />
                  <div className="absolute top-2.5 left-2.5">
                    <span className="inline-block px-2 py-0.5 rounded-md text-[11px] font-mono uppercase tracking-wider font-semibold bg-primary/95 text-primary-foreground shadow-sm backdrop-blur-xs">
                      {center.badge[lang]}
                    </span>
                  </div>
                </div>

                {/* Details Content */}
                <div className="md:col-span-7 p-4 sm:p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-serif text-lg sm:text-xl font-bold text-foreground">
                        {center.name}
                      </h3>
                      {isSelected && <span className="text-secondary font-mono text-[11px] uppercase tracking-wider font-semibold shrink-0 bg-secondary/10 px-2 py-0.5 rounded-md border border-secondary/20">
                          {copyDict.selectedBadge[lang]}
                        </span>}
                    </div>

                    <div className="mt-2.5 space-y-1.5 text-sm text-muted-foreground font-sans">
                      <div className="flex items-start gap-2">
                        <div className="p-1 rounded-md bg-secondary/10 shrink-0 mt-0.5">
                          <MapPin className="size-3.5 text-secondary" />
                        </div>
                        <span className="text-foreground/90 leading-snug text-xs sm:text-sm">{center.address}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="p-1 rounded-md bg-secondary/10 shrink-0">
                          <Phone className="size-3.5 text-secondary" />
                        </div>
                        <a href={`tel:${center.phone.replace(/[^0-9]/g, "")}`} className="hover:text-secondary font-medium text-foreground/90 transition-colors text-xs sm:text-sm" onClick={e => e.stopPropagation()}>
                          {center.phone}
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Action Bar & Program Chips */}
                  <div className="mt-3 pt-3 border-t border-border flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                    <div className="space-y-1">
                      <span className="font-mono text-xs text-muted-foreground block">
                        {center.specs[lang]}
                      </span>

                      {programsOffered && <PostsProvider wp_query={{
                    post_type: "program",
                    per_page: 20,
                    status: ["publish"]
                  }}>
                          <MatchedProgramBadges programsOffered={programsOffered} />
                        </PostsProvider>}
                    </div>

                    <div className="flex flex-wrap items-center gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
                      {matchedPost?.link && <Button asChild variant="outline" size="sm" className="h-8 text-xs font-sans">
                          <Link to={matchedPost.link}>
                            {copyDict.viewDetails[lang]}
                          </Link>
                        </Button>}
                      <Button asChild variant="outline" size="sm" className="h-8 text-xs font-sans">
                        <Link to={center.directionsUrl} newTab>
                          <Navigation className="size-3 text-secondary mr-1" />
                          {copyDict.getDirections[lang]}
                        </Link>
                      </Button>
                      <Button asChild size="sm" className="h-8 text-xs bg-primary text-primary-foreground hover:bg-primary/90 font-sans">
                        <Link to="/FreeTrialContactPage">
                          {copyDict.bookTrial[lang]}
                          <ChevronRight className="size-3 ml-0.5" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>;
    })}
    </>;
}
export default function Centers() {
  const [lang, setLang] = useState<'vi' | 'en'>('vi');
  const [selectedCenterId, setSelectedCenterId] = useState<string>("center-tan-mai");
  const prefersReducedMotion = useReducedMotion();
  useEffect(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("roboto_lang");
      if (stored === "en" || stored === "vi") {
        setLang(stored);
      }
    }
    const handleCustomLang = (event: Event) => {
      const customEvent = event as CustomEvent<{
        lang: 'vi' | 'en';
      }>;
      if (customEvent.detail && (customEvent.detail.lang === 'vi' || customEvent.detail.lang === 'en')) {
        setLang(customEvent.detail.lang);
      } else {
        const stored = localStorage.getItem("roboto_lang");
        if (stored === "en" || stored === "vi") setLang(stored);
      }
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "roboto_lang" && (e.newValue === "vi" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomLang);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomLang);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const activeCenter = centersData.find(c => c.id === selectedCenterId) || centersData[0];
  return <section id="our-centers" data-nav="light" className="relative w-full bg-background text-foreground py-20 md:py-24 border-b border-border overflow-hidden">
      {/* Background Blueprint Grid Motif */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] [background-size:32px_32px] [background-image:linear-gradient(to_right,currentColor_1px,transparent_1px),linear-gradient(to_bottom,currentColor_1px,transparent_1px)]" aria-hidden="true" />

      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 md:px-12 lg:px-16 xl:px-24">
        {/* Decorative Floating Mascot (Static, Non-interactive) */}
        <div className="hidden sm:block absolute top-0 right-5 sm:right-8 md:right-12 lg:right-16 xl:right-24 z-0 pointer-events-none select-none" aria-hidden="true">
          <div className="relative w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32">
            <img src={defaultMascot.src} data-wvc-srcset={defaultMascot.srcset} data-wvc-sizes={defaultMascot.sizes} alt="" loading="lazy" decoding="async" className="w-full h-full object-contain pointer-events-none select-none bg-transparent drop-shadow-sm" />
          </div>
        </div>

        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16 sm:pr-28 md:pr-36 lg:pr-0">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-xs bg-secondary" />
            <span className="font-mono text-xs uppercase tracking-widest text-secondary font-semibold">
              {copyDict.eyebrow[lang]}
            </span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground text-balance">
            {copyDict.heading[lang]}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
            {copyDict.description[lang]}
          </p>
        </div>

        {/* Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Interactive Map Visual Side (5 Cols) */}
          <div className="lg:col-span-5 order-2 lg:order-1 sticky lg:top-28">
            <div className="bg-card border border-border rounded-xl shadow-md p-4 sm:p-6 overflow-hidden">
              <div className="flex items-center justify-between pb-4 border-b border-border mb-4">
                <div className="flex items-center gap-2">
                  <div className="p-1 rounded-md bg-secondary/10">
                    <Compass className="size-4 text-secondary" />
                  </div>
                  <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground font-medium">
                    {copyDict.mapTitle[lang]}
                  </span>
                </div>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg text-xs font-mono font-medium bg-secondary/10 text-secondary">
                  <span className="size-1.5 rounded-lg bg-secondary animate-pulse" />
                  {copyDict.activeBadge[lang]}
                </span>
              </div>

              {/* Real Embedded Google Maps Iframe */}
              <div className="relative aspect-[4/3] w-full rounded-lg bg-muted border border-border overflow-hidden">
                <iframe title={`Google Map - ${activeCenter.name}`} src={`https://maps.google.com/maps?q=${encodeURIComponent(activeCenter.address)}&t=&z=15&ie=UTF8&iwloc=&output=embed`} className="w-full h-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>

              {/* Active Center Quick Spec Overview */}
              <div className="mt-5 p-4 rounded-lg bg-muted/50 border border-border">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <span className="font-mono text-xs uppercase tracking-wider text-secondary font-semibold">
                      {copyDict.currentlyFocused[lang]}
                    </span>
                    <h3 className="font-serif text-base font-bold text-foreground mt-0.5">
                      {activeCenter.name}
                    </h3>
                  </div>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-mono font-medium bg-secondary/10 text-secondary border border-secondary/20">
                    {activeCenter.badge[lang]}
                  </span>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground font-mono">
                  <div className="p-0.5 rounded-sm bg-secondary/10">
                    <CheckCircle2 className="size-3.5 text-secondary shrink-0" />
                  </div>
                  <span>{activeCenter.specs[lang]}</span>
                </div>
                <div className="mt-4 pt-3 border-t border-border flex items-center justify-between gap-4">
                  <div className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{copyDict.hotlineLabel[lang]}</span>{" "}
                    <a href={`tel:${activeCenter.phone.replace(/[^0-9]/g, "")}`} className="hover:text-secondary font-medium transition-colors">
                      {activeCenter.phone}
                    </a>
                  </div>
                  <Button asChild size="sm" variant="default" className="text-xs h-8">
                    <Link to={activeCenter.directionsUrl} newTab>
                      <Navigation className="size-3 mr-1" />
                      {copyDict.getDirections[lang]}
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Center Cards Side (7 Cols) */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <PostsProvider className="space-y-3.5" wp_query={{
            post_type: "center",
            per_page: 20,
            status: ["publish"]
          }}>
              <CenterCardsList selectedCenterId={selectedCenterId} setSelectedCenterId={setSelectedCenterId} lang={lang} />
            </PostsProvider>
          </div>
        </div>

        {/* Bottom Tour Booking Strip */}
        <div className="mt-14 p-6 sm:p-8 rounded-xl bg-muted/40 border border-border flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-start sm:items-center gap-4">
            <div className="size-12 rounded-lg bg-secondary/10 border border-secondary/20 flex items-center justify-center text-primary shadow-xs shrink-0">
              <Building2 className="size-6 text-secondary" />
            </div>
            <div>
              <h4 className="font-serif text-lg font-bold text-foreground">
                {copyDict.tourHeading[lang]}
              </h4>
              <p className="text-sm text-muted-foreground mt-0.5 font-sans">
                {copyDict.tourDesc[lang]}
              </p>
            </div>
          </div>
          <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 shrink-0 font-medium px-6 py-2.5 shadow-sm">
            <Link to="/FreeTrialContactPage">
              {copyDict.tourCta[lang]}
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}